/**
 * Customer Journey visualization component.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type RevenueCustomer, type CustomerJourneyData } from '../api';
import { Notice } from './Panels';

const CHANNEL_COLORS: Record<string, string> = {
  organic: 'var(--cat-4)',
  paid: 'var(--cat-1)',
  social: 'var(--cat-3)',
  email: 'var(--cat-5)',
  referral: 'var(--cat-6)',
  direct: 'var(--cat-other)',
};

export function CustomerJourneys() {
  const [customers, setCustomers] = useState<RevenueCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [journey, setJourney] = useState<CustomerJourneyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [journeyFailure, setJourneyFailure] = useState<ApiFailure | null>(null);

  useEffect(() => {
    api.getRevenueCustomers(50)
      .then((data) => setCustomers(data.customers))
      .catch((error: unknown) => {
        setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
      });
  }, []);

  const loadJourney = useCallback(async (customerId: string) => {
    if (customerId === '') return;
    setLoading(true);
    setJourneyFailure(null);
    setSelectedCustomer(customerId);
    try {
      const data = await api.getCustomerJourney(customerId);
      setJourney(data.journey);
    } catch (error) {
      setJourney(null);
      setJourneyFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="page">
      <header className="intro">
        <h2>Customer Journeys</h2>
        <p>Trace the complete path from first touch to revenue for each customer.</p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {failure === null && customers.length === 0 ? (
        <div className="card empty">
          <h3>No customers yet</h3>
          <p>
            Customers appear when they make a purchase through a connected payment provider,
            or when you import a sales CSV from the Revenue page.
          </p>
        </div>
      ) : failure === null && (
        <div className="grid wide">
          {/* Customer list */}
          <section className="card">
            <header><h2>Customers</h2></header>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {customers.map((customer) => (
                <button
                  key={customer.id}
                  className="nav-item"
                  style={{
                    background: selectedCustomer === customer.id ? 'var(--accent-wash)' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    padding: '8px 10px',
                    color: 'var(--ink)',
                  }}
                  onClick={() => void loadJourney(customer.id)}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{customer.email}</div>
                    <div className="muted">{customer.name ?? 'No name'} · {new Date(customer.createdAt).toLocaleDateString()}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Journey timeline */}
          <section className="card">
            {journeyFailure !== null && <Notice tone="error" message={journeyFailure.message} remedy={journeyFailure.remedy} />}

            {loading && <p className="muted"><span className="spinner" /> Loading journey…</p>}

            {!loading && journey === null && selectedCustomer !== '' && (
              <div className="empty">
                <h3>No journey data</h3>
                <p className="muted">This customer has no tracked touchpoints yet.</p>
              </div>
            )}

            {!loading && journey !== null && (
              <>
                <div className="scorecards" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 16 }}>
                  <div className="scorecard">
                    <div className="label">Touchpoints</div>
                    <div className="value" style={{ fontSize: 22 }}>{journey.touchpoints.length}</div>
                  </div>
                  <div className="scorecard">
                    <div className="label">Total Revenue</div>
                    <div className="value" style={{ fontSize: 22 }}>${journey.totalRevenue.toFixed(2)}</div>
                  </div>
                  <div className="scorecard">
                    <div className="label">First Touch</div>
                    <div className="meta">{new Date(journey.firstTouchAt).toLocaleDateString()}</div>
                  </div>
                  <div className="scorecard">
                    <div className="label">Conversion</div>
                    <div className="meta">{journey.conversionAt ? new Date(journey.conversionAt).toLocaleDateString() : 'None'}</div>
                  </div>
                </div>

                <div className="timeline">
                  {journey.touchpoints.map((tp) => (
                    <div key={tp.id} className="timeline-item">
                      <div className="timeline-dot" style={{ background: CHANNEL_COLORS[tp.channel] ?? 'var(--cat-other)' }} />
                      <div className="timeline-content">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span style={{ fontWeight: 500 }}>
                            {tp.channel.charAt(0).toUpperCase() + tp.channel.slice(1)}
                            {tp.source ? ` — ${tp.source}` : ''}
                          </span>
                          <span className="muted">{new Date(tp.occurredAt).toLocaleString()}</span>
                        </div>
                        {tp.campaign && <div className="muted">Campaign: {tp.campaign}</div>}
                        <div className="muted">{tp.landingPage}</div>
                      </div>
                    </div>
                  ))}

                  {journey.conversionAt && (
                    <div className="timeline-item">
                      <div className="timeline-dot" style={{ background: 'var(--warning)' }} />
                      <div className="timeline-content">
                        <div style={{ fontWeight: 600, color: 'var(--warning-text)' }}>
                          Converted — ${journey.totalRevenue.toFixed(2)}
                        </div>
                        <div className="muted">{new Date(journey.conversionAt).toLocaleString()}</div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
